import { useEffect, useRef, useState } from "react";
import { animate, motion, useInView } from "framer-motion";
import { ArrowUpRight, Check, Info } from "lucide-react";
import { PROGRAMS, PROGRAM_NOTE } from "@/data/services";
import { SectionHead, Reveal, EASE } from "@/components/ui/motion";
import { waLink } from "@/lib/config";

const CountUp = ({ to, prefix, suffix }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.5 });
  const [val, setVal] = useState(to);
  useEffect(() => {
    if (!inView) return undefined;
    const controls = animate(0, to, { duration: 1.8, ease: EASE, onUpdate: (v) => setVal(Math.round(v)) });
    return () => controls.stop();
  }, [inView, to]);
  return (
    <span ref={ref} className="inline-flex items-baseline gap-2 whitespace-nowrap">
      {prefix && <span className="serif-it text-2xl sm:text-3xl">{prefix}</span>}
      <span className="font-display font-extrabold tabular-nums tracking-[-0.05em]">{val}{suffix}</span>
    </span>
  );
};

const Bar = ({ label, value, width, delay, strong }) => (
  <div>
    <div className="flex justify-between text-[12.5px] font-semibold"><span className="text-ice">{label}</span><span className={strong ? "text-sky-brand" : "text-white"}>{value}</span></div>
    <div className="mt-2 h-3 overflow-hidden rounded-full bg-white/10">
      <motion.div initial={{ width: 0 }} whileInView={{ width }} viewport={{ once: true, amount: 0.5 }} transition={{ duration: 1.4, ease: EASE, delay }} className={`h-full rounded-full ${strong ? "bg-sky-brand" : "bg-white/50"}`} />
    </div>
  </div>
);

const DiscountBars = () => (
  <div className="relative mt-8 space-y-4 rounded-2xl border border-white/15 bg-white/5 p-5" data-testid="program-discount-bars">
    <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-sky-brand">Ilustrasi potongan maksimal*</p>
    <Bar label="Total tagihan" value="100%" width="100%" delay={0.1} />
    <Bar label="Dibayar setelah potongan" value="mulai 50%" width="50%" delay={0.4} strong />
  </div>
);

const ProgramCard = ({ p, featured, i }) => (
  <motion.article
    initial={{ opacity: 0, y: 40 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.15 }}
    transition={{ duration: 0.9, ease: EASE, delay: i * 0.1 }}
    whileHover={{ y: -6 }}
    className={`group relative flex h-full flex-col overflow-hidden rounded-[1.75rem] p-7 sm:p-9 ${featured ? "bg-slateb text-white" : "card"}`}
    data-testid={`program-card-${p.key}`}
  >
    <span aria-hidden="true" className={`pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full blur-3xl transition-transform duration-1000 group-hover:scale-150 ${featured ? "bg-sky-brand/30" : "bg-sky-brand/15"}`} />
    <div className={featured ? "relative grid gap-x-14 lg:grid-cols-2" : "relative flex flex-1 flex-col"}>
    <div className="flex flex-col">
    <div className="relative flex items-start justify-between gap-4">
      <span className={`text-[12px] font-bold uppercase tracking-[0.2em] ${featured ? "text-sky-brand" : "text-accentb"}`}>Program {p.no}</span>
      {featured && <span className="rounded-full bg-sky-brand px-3 py-1 text-[11px] font-bold text-ink">Paling diminati</span>}
    </div>
    <div className={`relative mt-6 ${featured ? "text-[84px] text-sky-brand sm:text-[120px]" : "text-[64px] text-accentb sm:text-[76px]"} leading-[0.9]`}>
      <CountUp to={p.stat} prefix={p.statPrefix} suffix={p.statSuffix} />
    </div>
    <p className={`relative mt-2 text-[13px] font-semibold ${featured ? "text-ice" : "text-ink-soft"}`}>{p.statLabel}</p>
    <h3 className={`relative mt-8 font-display font-bold leading-tight ${featured ? "text-3xl !text-white sm:text-4xl" : "text-2xl"}`}>{p.title}</h3>
    <p className={`relative mt-4 text-[15px] leading-relaxed ${featured ? "text-ice" : "text-ink"}`}>{p.text}</p>
    {featured && <DiscountBars />}
    </div>
    <div className="flex flex-1 flex-col">
    <ul className={`relative space-y-2.5 ${featured ? "mt-8 lg:mt-16" : "mt-6"}`}>
      {p.points.map((pt) => (
        <li key={pt} className={`flex gap-2.5 text-[14px] leading-snug ${featured ? "text-white" : "text-ink"}`}>
          <Check size={16} strokeWidth={2.5} className={`mt-0.5 shrink-0 ${featured ? "text-sky-brand" : "text-accentb"}`} />{pt}
        </li>
      ))}
    </ul>
    <p className={`relative mt-6 border-t pt-5 text-[13.5px] italic ${featured ? "border-white/15 text-ice" : "border-line text-ink-soft"}`}>{p.fit}</p>
    {featured && (
      <ol className="relative mt-8 grid gap-3 sm:grid-cols-3" data-testid="program-diskon-steps">
        {["Analisa total tagihan", "Negosiasi nilai pelunasan", "Bayar & terima surat lunas"].map((s, k) => (
          <motion.li key={s} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.5 }} transition={{ duration: 0.7, ease: EASE, delay: 0.2 + k * 0.12 }} className="rounded-2xl border border-white/15 bg-white/5 p-4">
            <span className="serif-it text-3xl text-sky-brand">0{k + 1}</span>
            <span className="mt-2 block text-[13.5px] font-semibold leading-snug text-white">{s}</span>
          </motion.li>
        ))}
      </ol>
    )}
    <div className="relative mt-auto pt-7">
      <a href={waLink("default", p.wa)} target="_blank" rel="noopener noreferrer" className={`btn ${featured ? "btn-sky" : "btn-slate"}`} data-testid={`program-wa-${p.key}`}>
        Tanya {p.title.replace("Program ", "")} <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:rotate-45" />
      </a>
    </div>
    </div>
    </div>
  </motion.article>
);

export const Programs = () => (
  <section className="relative overflow-hidden bg-ice py-20 sm:py-28 lg:py-36 grain" data-testid="programs-section">
    <div className="wrap relative">
      <SectionHead
        eyebrow="Program Penyelesaian"
        title={<>Tiga program, satu tujuan: <span className="serif-it font-normal text-accentb">tagihan lebih ringan.</span></>}
        desc="Pilih program sesuai kondisi Anda. Tim kami membantu menilai program yang paling realistis berdasarkan status akun dan kemampuan bayar."
      />
      <div className="mt-14 grid grid-cols-1 gap-5 lg:mt-20 lg:grid-cols-12">
        <div className="lg:col-span-12"><ProgramCard p={PROGRAMS[0]} featured i={0} /></div>
        <div className="lg:col-span-6"><ProgramCard p={PROGRAMS[1]} i={1} /></div>
        <div className="lg:col-span-6"><ProgramCard p={PROGRAMS[2]} i={2} /></div>
      </div>
      <Reveal className="mt-10 flex flex-col items-start justify-between gap-6 rounded-3xl border border-line bg-white/70 p-6 backdrop-blur sm:p-8 lg:flex-row lg:items-center">
        <p className="flex max-w-3xl gap-3 text-[13.5px] leading-relaxed text-ink" data-testid="programs-disclaimer">
          <Info size={18} className="mt-0.5 shrink-0 text-accentb" />{PROGRAM_NOTE} Program tidak menghapus catatan SLIK secara otomatis.
        </p>
        <a href={waLink()} target="_blank" rel="noopener noreferrer" className="btn btn-slate shrink-0" data-testid="programs-cta">
          Diskusikan Pilihan Anda <ArrowUpRight size={16} />
        </a>
      </Reveal>
    </div>
  </section>
);
